/**
 * Battle scene and HUD tunables (design doc §6, §19, §20; owner decision
 * 2026-10-05: Lantern Hour's stage, Saturday Morning Smackdown's hits, the
 * Storybook Diorama's HUD). First guesses, to be judged on the playtest
 * devices.
 */

/** Where the two fighters stand on the stage (world units; −z is towards the camera). */
export const HOMES = {
  /** The player's squishy: near left. */
  mine: { x: -2.15, z: -1.2 }, // TUNE
  /** The other side: further away, right. */
  theirs: { x: 2.15, z: 1.9 }, // TUNE
} as const;

export const FIGHTER = {
  /** Extra scale on the squishies (a close-up, like the gallery's). */
  scale: 2.7, // TUNE
  /**
   * Big squishies are drawn a little smaller in battle (#652), so the camera
   * never backs off to fit them: above `knee` (battle units, hat included)
   * only `keep` of the extra height stays, and nobody stands taller than
   * `maxHeight`. Most species (about 5 or less) are drawn as they are.
   */
  tall: { knee: 5, keep: 0.25, maxHeight: 5.6 }, // TUNE
  /**
   * Three-quarter facing: each face turns between "at the other fighter" (1)
   * and "at the camera" (0), so both faces always show.
   */
  facing: { mine: 0.5, theirs: 0.62 }, // TUNE
  /** The player's Keeper stands at their squishy's back, nearly upright under the low camera. */
  keeper: { offset: { x: 1.25, z: 3.4 }, yaw: -0.25, leanShare: 0.3 }, // TUNE
  /**
   * A fence segment standing in for a squishy (#203): the map's fence look,
   * built `length` long and scaled up to about a squishy's height.
   */
  fence: { length: 0.6, scale: 7 }, // TUNE
} as const;

/** Timings for playing the server's resolved log back, ms. */
export const PLAYBACK = {
  /** A move name shows this long before its result lands. */
  moveMs: 650, // TUNE
  /** A hit: squash, energy bar drop, callout. */
  hitMs: 800, // TUNE
  missMs: 700, // TUNE
  /** Heals, stat changes and statuses: a bounce or wobble plus a line. */
  effectMs: 700, // TUNE
  /** Flopping over when tuckered out. */
  tuckeredMs: 1100, // TUNE
  swapMs: 700, // TUNE
  /** Offering a Heart Charm: a hopeful pause, then a bounce or a wiggle. */
  captureMs: 1300, // TUNE
  /** A potion (#214): a cosy sip, a happy wiggle, and the sparkle shield. */
  itemMs: 1000, // TUNE
  /** A raid replay (#16) rests on its first turn this long before it plays. */
  replayLeadMs: 900, // TUNE
  /** Breather before the result screen. */
  endMs: 900, // TUNE
} as const;

/**
 * Render-on-demand pacing (tech spec §6): while only breathing animates, a
 * frame is asked for at most this often; while a move plays, every frame.
 * Asks land on display ticks, so this is a 30 fps interval less half a
 * 120 Hz tick: every 2nd tick at 60 Hz and every 4th at 120 Hz, both 30 fps,
 * with margin for timer jitter. A full 33.3 ms would round to every 3rd
 * tick (20 fps) at 60 Hz; exactly 25 ms sits on the 120 Hz boundary.
 */
export const BREATHING_FRAME_MS = 29; // TUNE: 30 fps is plenty for a slow breath

/** A submit that never reached the server is sent once more, with the same key, after this long. */
export const RETRY_AFTER_MS = 1200; // TUNE: a phone's radio often comes back within a second

/**
 * Saturday Morning Smackdown's hits (owner decision 2026-10-05): fractions are
 * of the step's length (`PLAYBACK`), distances in world units, angles in
 * radians.
 */
export const CHOREO = {
  /** Squash and stretch exaggeration (1 is the map's gentle squish). */
  exaggeration: 1.5, // TUNE: B's 1.6 trimmed a little for readability
  dash: {
    /** Anticipation (pull back and squat) before the dash, fraction of the move step. */
    windup: 0.36, // TUNE
    /** The dash itself, fraction of the move step; the rest is the brace at arm's length. */
    travel: 0.26, // TUNE
  },
  /**
   * Hit-stop, ms (longer for a super hit): when a hit lands, both fighters
   * hold their contact pose this long before the knockback.
   */
  hitStop: 120, // TUNE
  knockback: { distance: 1.2 }, // TUNE
  dodge: { side: 0.9 }, // TUNE
  /** How far a tuckered-out squishy flops over and sinks. */
  faint: { roll: 1.35, sink: 0.1 }, // TUNE
  swap: { out: 0.45 }, // TUNE: fraction of the swap step spent hopping out
  swapIn: { height: 3 }, // TUNE: drops from this high
  /** The Heart Charm: when it lands, when the wobble ends, when the result shows (fractions). */
  charm: { throwAt: 0.3, wobbleAt: 0.42, resultAt: 0.78, shrink: 0.2, lift: 0.9 }, // TUNE
  /** Hit strength per effectiveness tier (`BATTLE_RULES.effectiveness`). */
  strength: { super: 1.45, good: 1.2, normal: 1, weak: 0.65 } as Readonly<Record<string, number>>, // TUNE
  /** Standing ready: lean in a touch, a light bob on the toes. */
  ready: { lean: 0.06, bob: 0.045, bobHz: 1.3, otherPhase: 0.37 }, // TUNE: otherPhase keeps the two out of step
  /** A fighter left at arm's length (a dash that didn't land) hops home this fast, ms. */
  returnMs: 320, // TUNE
  /** Reduced motion: moves this size (dashes become a short lunge), no twirls, shake, flash or hit-stop. */
  reduced: { size: 0.45, reach: 0.35 }, // TUNE
} as const;

/** The battle camera's tunables (`BATTLE_CAMERA`, where each one is described). */
export interface BattleCameraConfig {
  pitch: number;
  yaw: number;
  fov: number;
  minZ: number;
  maxZ: number;
  aim: { readonly x: number; readonly y: number; readonly z: number };
  feetY: number;
  fitHalfWidth: number;
  fitHeight: number;
  rimMargin: number;
  minDistance: number;
  maxDistance: number;
  narrowSheetFrom: number;
  hitPush: number;
  followPush: number;
  koPush: number;
  shake: number;
  roll: number;
  focusShift: number;
  follow: number;
  shakeHz: number;
  shakeDecay: number;
  beatDecay: number;
}

/**
 * Lantern Hour's camera (owner decision 2026-10-05): low, swung a little to
 * the player's side, framing the fight into the safe region between the
 * HUD's pills and its bottom sheet. Angles in radians, distances in world units.
 */
export const BATTLE_CAMERA: Readonly<BattleCameraConfig> = {
  /** Tilt down from the horizon. */
  pitch: (11 * Math.PI) / 180, // TUNE
  /** Swing around the fight (0 looks along +z). */
  yaw: (-14 * Math.PI) / 180, // TUNE
  fov: 0.74, // TUNE
  minZ: 0.3, // TUNE
  maxZ: 300,
  /** Where it looks: a point at the fighters' feet, between them. */
  aim: { x: 0.35, y: 0.1, z: 0.5 }, // TUNE
  /** Where the aim lands on screen, as a fraction of the height from the top. */
  feetY: 0.56, // TUNE: low enough to use the room above the sheet
  /** The fight's box that must always fit in the safe region: half width and height. */
  fitHalfWidth: 3.9, // TUNE
  fitHeight: 3.6, // TUNE
  /**
   * The arena's reach (#652, `arenaReach`): the lowest row of the stage stays
   * `rimMargin` inside the ground's rim (room for the lean and a shake).
   * `minDistance` and `maxDistance` only bound it (a row that never meets the
   * ground has no reach of its own).
   */
  rimMargin: 2, // TUNE
  minDistance: 10, // TUNE
  maxDistance: 90, // TUNE
  /** From this window width (CSS px) the sheet is a centred card (battle.css), so the screen's bottom shows. */
  narrowSheetFrom: 900,
  /** Camera beats (B): push-in on a hit (fraction of distance), shake (world units), dutch roll (radians). */
  hitPush: 0.1, // TUNE
  followPush: 0.04, // TUNE
  koPush: 0.08, // TUNE
  shake: 0.12, // TUNE: light
  roll: (1.5 * Math.PI) / 180, // TUNE: only on super hits
  /** Leans the framing this far towards the fighter a step is about. */
  focusShift: 0.35, // TUNE
  /** Camera follow smoothing, per second (higher is snappier). */
  follow: 4.5, // TUNE
  /** Shake: wobbles per second and how fast it dies away. */
  shakeHz: 21, // TUNE
  shakeDecay: 9, // TUNE
  /** How fast a push-in and a beat decay back, per second. */
  beatDecay: 2.4, // TUNE
};

/**
 * A potion's sparkle shield (#214): a soft lavender bubble over the squishy
 * until the next hit lands. `fit` is its size against the squishy's.
 */
export const SHIELD_BUBBLE = {
  color: [0.82, 0.72, 1] as const, // TUNE
  glow: [0.42, 0.34, 0.62] as const, // TUNE
  alpha: 0.24, // TUNE
  fit: 1.25, // TUNE
};

/**
 * Finding a rare one (#391, owner decisions 2026-10-12: the mockup's reveal
 * beat). Each tier's length, words and size step (`scale`, rare = 1) are
 * shared data (`RARE_FIND_TIERS`); this is how a step looks here. It grows
 * with the step: more sparkles, a deeper dim, a bigger banner with a thicker
 * ring and one more chime note (the `find-*` cues). Index `scale - 1`; a step
 * past the end uses the last. A kind met before shows only the banner, for
 * `briefMs`, with no pause.
 */
export const RARE_REVEAL = {
  sparkles: [6, 12, 16, 20, 24, 28], // TUNE
  dim: [0.14, 0.22, 0.28, 0.32, 0.35, 0.38], // TUNE
  banner: [16, 18, 19, 20, 21, 22], // TUNE
  ring: [2, 3, 4, 5, 6, 6], // TUNE
  briefMs: 1000, // TUNE
  /** The banner's text grows on wide screens (an iPad), as in the mockup. */
  wideScreen: 500,
  wideBanner: 1.25, // TUNE
  /** The banner sits this far above the bottom sheet (CSS px). */
  sheetGap: 12,
  /** Sparkles stay this far above the sheet and below the name pills (CSS px). */
  sparkleClear: { sheet: 70, pills: 10 },
  /** Sparkles land this far out from the squishy's middle, in its half-heights. */
  sparkleReach: [1.2, 1.9] as const, // TUNE
  /** The fade out at the end (ms). */
  fadeMs: 250,
  /** Frames to wait for the arena to draw the squishy before dimming mid-screen instead. */
  waitFrames: 30,
  /** How long after a skipping tap lifts its click is swallowed (ms; it follows at once). */
  clickGraceMs: 350,
} as const;

/** A step's value from one of `RARE_REVEAL`'s lists (the last past the end). */
export function revealStep(list: readonly number[], scale: number): number {
  return list[Math.min(Math.max(scale, 1), list.length) - 1] ?? 0;
}

/**
 * Befriending a rare one (#391): hearts that fall over the new friend, by the
 * tier's size step (index `scale - 1`, the last past the end); fewer, and only
 * fading in, with Reduce Motion. They keep `heartClear` px off the pills, and
 * three times that off the card.
 */
export const BEFRIEND_CHEER = {
  hearts: [8, 12, 16, 20, 24], // TUNE
  calmHearts: 6, // TUNE
  heartClear: 10,
} as const;
